import { useCallback, useState } from 'react';
import { useParams } from 'react-router';

const readStoredValue = (storageKey, defaultValue) => {
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    return stored === null ? defaultValue : JSON.parse(stored);
  } catch (error) {
    return defaultValue;
  }
};

/**
 * useState for what is open on a user's account page (inner tabs, filters, sections, rows).
 * Values are kept per user in sessionStorage, so they survive a page refresh and
 * sections unmounting while data reloads, and don't carry over to a new browser tab.
 */
export default function usePageState(name, defaultValue) {
  const { userId } = useParams();
  const storageKey = `account-page:${userId}:${name}`;

  const [state, setState] = useState(() => ({
    storageKey,
    value: readStoredValue(storageKey, defaultValue),
  }));

  const value = state.storageKey === storageKey ? state.value : readStoredValue(storageKey, defaultValue);

  const setValue = useCallback((nextValue) => {
    setState({ storageKey, value: nextValue });
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(nextValue));
    } catch (error) {
      // The page still works if storage is unavailable, it just won't survive a refresh
    }
  }, [storageKey]);

  return [value, setValue];
}
